import { ThemeVariant } from "@phoenix/config.ts";

export enum LogLevel {
  Error,
  Warning,
  Info,
  Debug
}

type LogLevelStrings = keyof typeof LogLevel;

export interface TestAuthConfig {
  enabled: boolean;
}

export interface AuthConfig {
  microsoftClientId: string;
  microsoftRedirectUrl: string;
  googleClientId: string;
  appleClientId: string;
  testAuth: TestAuthConfig;
}

export type ContentAppConfig = {
  domain: string;
};

export interface AppConfig {
  environment: string;
  buildHash: string;
  buildDate: string;
  appDomain: string;
  contentAppConfig: ContentAppConfig;
  cookieDomain: string;
  contentBaseUrl: string;
  appBaseUrl: string;
  apiBaseUrl: string;
  mfeAppUrl: string;
  mcpServerBaseUrl: string;
  authConfig: AuthConfig;
  logLevel: LogLevel;
  logKeysEnabled: Set<string>;
  applicationInsightsConnectionString: string;
  themeOverride: ThemeVariant | null;
  stripePublishableKey: string;
}

function getContentAppConfigFromUrl(source: string): ContentAppConfig {
  const splits = source.split("/", 2);

  return {
    domain: splits[0]
  };
}

export const appConfig: Readonly<AppConfig> = {
  environment: import.meta.env.MODE,
  buildHash: import.meta.env.VITE_BUILD_HASH,
  buildDate: import.meta.env.VITE_BUILD_DATE,
  appDomain: import.meta.env.VITE_APP_DOMAIN,
  contentAppConfig: getContentAppConfigFromUrl(import.meta.env.VITE_CONTENT_DOMAIN),
  cookieDomain: import.meta.env.VITE_COOKIE_DOMAIN,
  contentBaseUrl: import.meta.env.VITE_CONTENT_BASE_URL,
  appBaseUrl: import.meta.env.VITE_APP_BASE_URL,
  // The API is reached through a same-origin reverse proxy on the app's own host: each
  // frontend origin (app.*, ops.*, or the vite dev server) forwards `/api/v1/x` to the
  // backend as `/v1/x`. Deriving the base from the running origin keeps auth cookies
  // host-only per frontend and needs no per-environment API host configuration.
  // A getter because the content site's build-time page prerender imports this under Node.
  get apiBaseUrl(): string { return `${window.location.origin}/api`; },
  mfeAppUrl: import.meta.env.VITE_MFE_APP_URL,
  mcpServerBaseUrl: import.meta.env.VITE_MCP_SERVER_BASE_URL,
  authConfig: {
    microsoftClientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
    microsoftRedirectUrl: import.meta.env.VITE_AUTH_MICROSOFT_REDIRECT_URL,
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    appleClientId: import.meta.env.VITE_APPLE_CLIENT_ID,
    testAuth: {
      enabled: import.meta.env.VITE_TEST_AUTH_ENABLED === 'true'
    }
  },
  logLevel: LogLevel[import.meta.env.VITE_LOG_LEVEL as LogLevelStrings],
  logKeysEnabled: new Set<string>((["AuthProvider"] as string[]).map(s => s.toLowerCase())),
  applicationInsightsConnectionString: import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
  // themeOverride: "dark"
  themeOverride: null,
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
};

export const getContentUrl = (path: string) => `${appConfig.contentBaseUrl}${path}`;
export const getBaseContentUrl = () => getContentUrl("");
export const getAppUrl = (path: string) => `${appConfig.appBaseUrl}${path}`;
export const getBaseAppUrl = () => getAppUrl("");

export const getRunningSiteBaseUrl = () => {
  return `${window.location.protocol}//${window.location.host}`;
}
